/**
 * Email Flow Change Page
 * Allows admins to change the email flow a user is on
 */

import React, { useState, useEffect, useCallback } from 'react';
// ...existing code...
import { 
  Search, 
  RefreshCw, 
  User, 
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  RotateCcw,
  Save
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { 
  collection, 
  query, 
  where, 
  limit, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../services/firebase/config';


// Email flow configuration (mirror of backend)
const EMAIL_FLOWS: Record<string, { 
  name: string; 
  description: string; 
  trigger: string;
  applicableTiers: string[];
  steps: Array<{ step: number; templateId: string; delay: { minutes?: number; hours?: number; days?: number }; condition: string | null }> 
}> = {
  leadMagnet: {
    name: 'Lead Magnet Download',
    description: 'Nurture leads who downloaded e-book to free registration',
    trigger: 'ebook_download',
    applicableTiers: ['free'],
    steps: [
      { step: 1, templateId: 'lead-magnet-1', delay: { hours: 1 }, condition: null },
      { step: 2, templateId: 'lead-magnet-2', delay: { days: 2 }, condition: '!registered' },
      { step: 3, templateId: 'lead-magnet-3', delay: { days: 5 }, condition: '!registered' },
    ]
  },
  freeRegistration: {
    name: 'Free Registration Onboarding',
    description: 'Onboard free users and encourage upgrade',
    trigger: 'user_registered',
    applicableTiers: ['free'],
    steps: [
      { step: 1, templateId: 'free-welcome', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'free-ethics-course', delay: { days: 2 }, condition: '!upgraded' },
      { step: 3, templateId: 'free-testimonial', delay: { days: 5 }, condition: '!upgraded' },
      { step: 4, templateId: 'free-upgrade-benefits', delay: { days: 7 }, condition: '!upgraded' },
      { step: 5, templateId: 'free-AIreadiness', delay: { days: 10 }, condition: '!upgraded' },
    ]
  },
  paidMembership: {
    name: 'Paid Membership Onboarding',
    description: 'Onboard paid members and drive engagement',
    trigger: 'subscription_created',
    applicableTiers: ['standard', 'premium', 'corporate'],
    steps: [
      { step: 1, templateId: 'paid-welcome-standard', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'paid-maximize-membership', delay: { days: 2 }, condition: null },
      { step: 3, templateId: 'paid-first-sessions-standard', delay: { days: 5 }, condition: null },
      { step: 4, templateId: 'paid-testimonial', delay: { days: 7 }, condition: null },
      { step: 5, templateId: 'paid-feedback', delay: { days: 8 }, condition: null },
      { step: 6, templateId: 'paid-community-checkin', delay: { days: 14 }, condition: null },
    ]
  },
  cancellation: {
    name: 'Cancellation Flow',
    description: 'Handle subscription cancellations gracefully',
    trigger: 'subscription_cancelled',
    applicableTiers: ['free'],
    steps: [
      { step: 1, templateId: 'cancellation-early', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'cancellation-rejoin', delay: { days: 10 }, condition: null },
    ]
  },
  incompleteRegistration: {
    name: 'Incomplete Registration Recovery',
    description: 'Re-engage users who started but didn\'t complete registration',
    trigger: 'registration_abandoned',
    applicableTiers: ['free'],
    steps: [
      { step: 1, templateId: 'incomplete-registration', delay: { hours: 1 }, condition: null },
    ]
  },
  twelveMonthPackage: {
    name: '12-Month Package Onboarding',
    description: 'Onboard 12-month package users with reminders and engagement',
    trigger: 'twelve_month_package_purchased',
    applicableTiers: ['twelve_month_package'],
    steps: [
      { step: 1, templateId: 'twelve-month-welcome', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'twelve-month-maximize', delay: { days: 2 }, condition: null },
      { step: 3, templateId: 'twelve-month-first-month-checkin', delay: { days: 30 }, condition: null },
      { step: 4, templateId: 'twelve-month-midpoint', delay: { days: 180 }, condition: null },
      { step: 5, templateId: 'twelve-month-90-days-left', delay: { days: 275 }, condition: null },
      { step: 6, templateId: 'twelve-month-30-days-left', delay: { days: 335 }, condition: null },
      { step: 7, templateId: 'twelve-month-renewal-reminder', delay: { days: 355 }, condition: null },
    ]
  },
  stackcommerceLifetime: {
    name: 'StackCommerce Lifetime Onboarding',
    description: 'Onboard lifetime members with ongoing engagement and skills assessment reminders',
    trigger: 'stackcommerce_lifetime_activated',
    applicableTiers: ['stackcommerce_lifetime'],
    steps: [
      { step: 1, templateId: 'lifetime-welcome', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'lifetime-maximize', delay: { days: 2 }, condition: null },
      { step: 3, templateId: 'lifetime-skills-assessment-reminder', delay: { days: 7 }, condition: '!skillsAssessmentCompleted' },
      { step: 4, templateId: 'lifetime-first-month-checkin', delay: { days: 30 }, condition: null },
      { step: 5, templateId: 'lifetime-skills-assessment-quarterly', delay: { days: 90 }, condition: null },
      { step: 6, templateId: 'lifetime-6-month-checkin', delay: { days: 180 }, condition: null },
      { step: 7, templateId: 'lifetime-skills-assessment-biannual', delay: { days: 180 }, condition: null },
      { step: 8, templateId: 'lifetime-annual-checkin', delay: { days: 365 }, condition: null },
    ]
  },
};

interface UserEmailFlow {
  currentFlow: string;
  currentStep: number;
  nextEmailAt: Date | null;
  isActive: boolean;
  startedAt: Date | null;
  completedAt: Date | null;
  stoppedAt: Date | null;
  stoppedReason: string | null;
  lastEmailSent: Date | null;
}

interface UserData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  subscriptionTier: string;
  emailFlow?: UserEmailFlow;
  createdAt: Date;
}

const parseFlowData = (flowData: any): UserEmailFlow | undefined => {
  if (!flowData) return undefined;
  return {
    currentFlow: flowData.currentFlow,
    currentStep: flowData.currentStep,
    nextEmailAt: flowData.nextEmailAt?.toDate?.() || null,
    isActive: flowData.isActive,
    startedAt: flowData.startedAt?.toDate?.() || null,
    completedAt: flowData.completedAt?.toDate?.() || null,
    stoppedAt: flowData.stoppedAt?.toDate?.() || null,
    stoppedReason: flowData.stoppedReason || null,
    lastEmailSent: flowData.lastEmailSent?.toDate?.() || null,
  };
};

const getFlowDocField = (_flowKey: string): string => 'emailFlow';

export const EmailFlowChangePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get('userId');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserData[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [flowFilter, setFlowFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Load user by ID on mount
  useEffect(() => {
    if (initialUserId) {
      loadUserById(initialUserId);
    }
  }, [initialUserId]);

  // Load user by ID
  const loadUserById = async (userId: string) => {
    setLoading(true);
    setError(null);

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const userData: UserData = {
          id: userDoc.id,
          email: data.email || '',
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: data.displayName,
          subscriptionTier: data.subscriptionTier || 'free',
          emailFlow: parseFlowData(data.emailFlow),
          createdAt: data.createdAt?.toDate?.() || new Date(),
        };
        
        setSelectedUser(userData);
        const activeFlow = userData.emailFlow;
        if (activeFlow) {
          setSelectedFlow(activeFlow.currentFlow);
          setSelectedStep(activeFlow.currentStep);
        }
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  // Search users by email or filter
  const searchUsers = useCallback(async () => {
    if (!searchTerm.trim() && !flowFilter && !statusFilter) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    setError(null);

    try {
      let users: UserData[] = [];
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const emailQuery = query(
          collection(db, 'users'),
          where('email', '>=', searchLower),
          where('email', '<=', searchLower + '\uf8ff'),
          limit(10)
        );
        const emailSnapshot = await getDocs(emailQuery);
        emailSnapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          users.push({
            id: docSnap.id,
            email: data.email || '',
            firstName: data.firstName,
            lastName: data.lastName,
            displayName: data.displayName,
            subscriptionTier: data.subscriptionTier || 'free',
            emailFlow: parseFlowData(data.emailFlow),
            createdAt: data.createdAt?.toDate?.() || new Date(),
          });
        });
      } else if (flowFilter || statusFilter) {
        // Fetch users by filters only (limit 50, filter in-memory)
        const userQuery = collection(db, 'users');
        const q = query(userQuery, limit(50));
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          users.push({
            id: docSnap.id,
            email: data.email || '',
            firstName: data.firstName,
            lastName: data.lastName,
            displayName: data.displayName,
            subscriptionTier: data.subscriptionTier || 'free',
            emailFlow: parseFlowData(data.emailFlow),
            createdAt: data.createdAt?.toDate?.() || new Date(),
          });
        });
      }
      // Apply filter logic in-memory
      users = users.filter(user => {
        const anyFlow = user.emailFlow;
        if (flowFilter) {
          const flows = [user.emailFlow];
          if (!flows.some(f => f?.currentFlow === flowFilter)) return false;
        }
        if (statusFilter === 'inprogress' && (!anyFlow || !anyFlow.isActive)) return false;
        if (statusFilter === 'completed' && (!anyFlow || anyFlow.isActive)) return false;
        return true;
      }).slice(0, 10);
      setSearchResults(users);
      setShowDropdown(users.length > 0);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm, flowFilter, statusFilter]);

  // Select user
  const selectUser = (user: UserData) => {
    setSelectedUser(user);
    const activeFlow = user.emailFlow;
    setSelectedFlow(activeFlow?.currentFlow || null);
    setSelectedStep(activeFlow?.currentStep || 1);
    setError(null);
    setSuccess(null);
    setShowDropdown(false);
  };

  // Calculate next email time based on step delay
  const calculateNextEmailTime = (flowKey: string, step: number): Date => {
    const flow = EMAIL_FLOWS[flowKey];
    if (!flow) return new Date();

    const stepConfig = flow.steps.find(s => s.step === step);
    if (!stepConfig) return new Date();

    const now = new Date();
    const delay = stepConfig.delay;
    
    if (delay.minutes !== undefined) now.setMinutes(now.getMinutes() + delay.minutes);
    if (delay.hours) now.setHours(now.getHours() + delay.hours);
    if (delay.days) now.setDate(now.getDate() + delay.days);

    return now;
  };

  // Save flow changes
  const saveFlowChanges = async () => {
    if (!selectedUser || !selectedFlow) {
      setError('Please select a user and flow');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const nextEmailAt = calculateNextEmailTime(selectedFlow, selectedStep);

      const field = getFlowDocField(selectedFlow);
      await updateDoc(userRef, {
        [field]: {
          currentFlow: selectedFlow,
          currentStep: selectedStep,
          nextEmailAt: Timestamp.fromDate(nextEmailAt),
          isActive: true,
          startedAt: serverTimestamp(),
          completedAt: null,
          stoppedAt: null,
          stoppedReason: null,
        },
        updatedAt: serverTimestamp(),
      });

      setSuccess(`Email flow changed to "${EMAIL_FLOWS[selectedFlow].name}" starting at step ${selectedStep}`);
      
      // Reload user data
      await loadUserById(selectedUser.id);
    } catch (err) {
      console.error('Error saving flow changes:', err);
      setError('Failed to save flow changes');
    } finally {
      setSaving(false);
    }
  };

  // Stop flow
  const stopFlow = async () => {
    if (!selectedUser) return;

    const activeFlow = selectedUser.emailFlow;
    if (!activeFlow) return;

    setSaving(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const field = getFlowDocField(activeFlow.currentFlow);

      await updateDoc(userRef, {
        [`${field}.isActive`]: false,
        [`${field}.stoppedAt`]: serverTimestamp(),
        [`${field}.stoppedReason`]: 'admin_stopped',
        updatedAt: serverTimestamp(),
      });

      setSuccess('Email flow stopped');
      await loadUserById(selectedUser.id);
    } catch (err) {
      console.error('Error stopping flow:', err);
      setError('Failed to stop flow');
    } finally {
      setSaving(false);
    }
  };

  // Resume flow
  const resumeFlow = async () => {
    const stoppedFlow = selectedUser?.emailFlow;
    if (!selectedUser || !stoppedFlow) return;

    setSaving(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const field = getFlowDocField(stoppedFlow.currentFlow);
      const nextEmailAt = calculateNextEmailTime(stoppedFlow.currentFlow, stoppedFlow.currentStep);

      await updateDoc(userRef, {
        [`${field}.isActive`]: true,
        [`${field}.nextEmailAt`]: Timestamp.fromDate(nextEmailAt),
        [`${field}.stoppedAt`]: null,
        [`${field}.stoppedReason`]: null,
        updatedAt: serverTimestamp(),
      });

      setSuccess('Email flow resumed');
      await loadUserById(selectedUser.id);
    } catch (err) {
      console.error('Error resuming flow:', err);
      setError('Failed to resume flow');
    } finally {
      setSaving(false);
    }
  };

  // Clear flow
  const clearFlow = async () => {
    if (!selectedUser) return;

    if (!window.confirm('Are you sure you want to remove this user from all email flows?')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', selectedUser.id);

      await updateDoc(userRef, {
        emailFlow: null,
        updatedAt: serverTimestamp(),
      });

      setSuccess('User removed from all email flows');
      setSelectedFlow(null);
      setSelectedStep(1);
      await loadUserById(selectedUser.id);
    } catch (err) {
      console.error('Error clearing flow:', err);
      setError('Failed to clear flow');
    } finally {
      setSaving(false);
    }
  };

  // Format delay for display
  const formatDelay = (delay: { minutes?: number; hours?: number; days?: number }): string => {
    const parts: string[] = [];
    if (delay.days) parts.push(`${delay.days}d`);
    if (delay.hours) parts.push(`${delay.hours}h`);
    if (delay.minutes !== undefined) {
      if (delay.minutes === 0 && parts.length === 0) return 'Immediate';
      else if (delay.minutes > 0) parts.push(`${delay.minutes}m`);
    }
    return parts.join(' ') || 'Immediate';
  };

  // Get flow status badge
  const getFlowStatusBadge = (emailFlow?: UserEmailFlow) => {
    if (!emailFlow) {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">No Flow</span>;
    }
    if (emailFlow.completedAt) {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Completed</span>;
    }
    if (emailFlow.stoppedAt) {
      return <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">Stopped</span>;
    }
    if (emailFlow.isActive) {
      return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Active</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Paused</span>;
  };

  // Get recommended flows based on user tier
  const getRecommendedFlows = (tier: string): string[] => {
    return Object.entries(EMAIL_FLOWS)
      .filter(([_, flow]) => flow.applicableTiers.includes(tier))
      .map(([key]) => key);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Change Email Flow</h1>
              <p className="text-gray-600">Assign or modify the email flow for a user</p>
            </div>
            {/* Removed View Flows and Email Management buttons as requested */}
          </div>
        </div>

        {/* Search Section (only if no user preselected) */}
        {!initialUserId && (
          <Card className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* User Search Dropdown */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  placeholder="Type to search users..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="off"
                  onFocus={() => searchTerm && setShowDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowDropdown(false), 150);
                  }}
                />
                {/* Dropdown results */}
                {showDropdown && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                    {searchResults
                      .filter(user => {
                        const anyFlow = user.emailFlow;
                        if (flowFilter) {
                          const flows = [user.emailFlow];
                          if (!flows.some(f => f?.currentFlow === flowFilter)) return false;
                        }
                        if (statusFilter === 'inprogress' && (!anyFlow || !anyFlow.isActive)) return false;
                        if (statusFilter === 'completed' && (!anyFlow || anyFlow.isActive)) return false;
                        return true;
                      })
                      .map(user => (
                        <button
                          key={user.id}
                          onClick={() => {
                            selectUser(user);
                            setShowDropdown(false);
                            setSearchResults([]);
                            setSearchTerm(user.email);
                            setTimeout(() => {
                              setShowDropdown(false);
                            }, 0);
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-blue-50 ${selectedUser?.id === user.id ? 'bg-blue-100' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{user.email}</span>
                            <span className="text-xs text-gray-500">{user.firstName} {user.lastName}</span>
                            <span className="text-xs text-purple-600">{user.subscriptionTier}</span>
                            {getFlowStatusBadge(user.emailFlow)}
                          </div>
                        </button>
                      ))}
                    {searchResults.filter(user => {
                      const anyFlow = user.emailFlow;
                      if (flowFilter) {
                        const flows = [user.emailFlow];
                        if (!flows.some(f => f?.currentFlow === flowFilter)) return false;
                      }
                      if (statusFilter === 'inprogress' && (!anyFlow || !anyFlow.isActive)) return false;
                      if (statusFilter === 'completed' && (!anyFlow || anyFlow.isActive)) return false;
                      return true;
                    }).length === 0 && (
                      <div className="px-4 py-2 text-gray-500">No users match filters</div>
                    )}
                  </div>
                )}
              </div>
              {/* Flow Filter */}
              <div>
                <select
                  value={flowFilter}
                  onChange={e => setFlowFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Flows</option>
                  {Object.entries(EMAIL_FLOWS).map(([key, flow]) => (
                    <option key={key} value={key}>{flow.name}</option>
                  ))}
                </select>
              </div>
              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="inprogress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <Button onClick={() => {
                if (searchTerm) {
                  searchUsers();
                } else if (flowFilter || statusFilter) {
                  // Fetch users by filters only
                  (async () => {
                    setSearchLoading(true);
                    setError(null);
                    try {
                      let userQuery = collection(db, 'users');
                      let q = query(userQuery, limit(50));
                      const snapshot = await getDocs(q);
                      let users: UserData[] = [];
                      snapshot.docs.forEach(docSnap => {
                        const data = docSnap.data();
                        users.push({
                          id: docSnap.id,
                          email: data.email || '',
                          firstName: data.firstName,
                          lastName: data.lastName,
                          displayName: data.displayName,
                          subscriptionTier: data.subscriptionTier || 'free',
                          emailFlow: parseFlowData(data.emailFlow),
                          createdAt: data.createdAt?.toDate?.() || new Date(),
                        });
                      });
                      // Apply filter logic in-memory
                      users = users.filter(user => {
                        const anyFlow = user.emailFlow;
                        if (flowFilter) {
                          const flows = [user.emailFlow];
                          if (!flows.some(f => f?.currentFlow === flowFilter)) return false;
                        }
                        if (statusFilter === 'inprogress' && (!anyFlow || !anyFlow.isActive)) return false;
                        if (statusFilter === 'completed' && (!anyFlow || anyFlow.isActive)) return false;
                        return true;
                      }).slice(0, 10);
                      setSearchResults(users);
                      setShowDropdown(users.length > 0);
                    } catch (err) {
                      console.error('Error searching users:', err);
                      setError('Failed to search users');
                    } finally {
                      setSearchLoading(false);
                    }
                  })();
                } else {
                  setSearchResults([]);
                  setShowDropdown(false);
                }
              }} disabled={searchLoading}>
                {searchLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Search
              </Button>
            </div>
          </Card>
        )}

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading user data...</span>
          </div>
        )}

        {/* User Selected - Show Flow Options */}
        {selectedUser && !loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - User Info & Current Flow */}
            <div className="lg:col-span-1 space-y-6">
              {/* User Info */}
              <Card>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Selected User</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{selectedUser.firstName} {selectedUser.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Subscription</p>
                    <p className="font-medium">{selectedUser.subscriptionTier}</p>
                  </div>
                </div>
              </Card>

              {/* Current Flows */}
              <Card>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Current Flows</h3>
                {(() => {
                  const flows: Array<{ label: string; flow: UserEmailFlow }> = [];
                  if (selectedUser.emailFlow) flows.push({ label: 'TrainedUp', flow: selectedUser.emailFlow });

                  if (flows.length === 0) {
                    return <div className="text-center py-4"><p className="text-gray-500">No active email flow</p></div>;
                  }

                  return (
                    <div className="space-y-4">
                      {flows.map(({ label, flow }) => (
                        <div key={label} className="space-y-2 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium">{EMAIL_FLOWS[flow.currentFlow]?.name || flow.currentFlow}</span>
                              <span className="ml-2 text-xs text-gray-400">{label}</span>
                            </div>
                            {getFlowStatusBadge(flow)}
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Current Step</p>
                            <p className="font-medium">{flow.currentStep}</p>
                          </div>
                          {flow.nextEmailAt && (
                            <div>
                              <p className="text-sm text-gray-500">Next Email</p>
                              <p className="font-medium">{flow.nextEmailAt.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                      ))}
                      {/* Flow Controls */}
                      <div className="pt-2 space-y-2">
                        {flows.some(({ flow }) => flow.isActive) ? (
                          <Button variant="secondary" size="sm" className="w-full" onClick={stopFlow} disabled={saving}>
                            <Pause className="w-4 h-4 mr-2" />Stop Active Flow
                          </Button>
                        ) : (
                          <Button variant="primary" size="sm" className="w-full" onClick={resumeFlow} disabled={saving}>
                            <Play className="w-4 h-4 mr-2" />Resume Flow
                          </Button>
                        )}
                        <Button variant="secondary" size="sm" className="w-full text-red-600 hover:bg-red-50" onClick={clearFlow} disabled={saving}>
                          <RotateCcw className="w-4 h-4 mr-2" />Remove from All Flows
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </Card>
            </div>

            {/* Right Column - Flow Selection */}
            <div className="lg:col-span-2">
              <Card>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Assign New Flow</h3>
                
                {/* Recommended Flows */}
                {getRecommendedFlows(selectedUser.subscriptionTier).length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-3">
                      Recommended for <span className="font-medium">{selectedUser.subscriptionTier}</span> users:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {getRecommendedFlows(selectedUser.subscriptionTier).map(flowKey => (
                        <button
                          key={flowKey}
                          onClick={() => {
                            setSelectedFlow(flowKey);
                            setSelectedStep(1);
                          }}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            selectedFlow === flowKey
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {EMAIL_FLOWS[flowKey].name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Flows */}
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-gray-700">All Available Flows:</p>
                  {Object.entries(EMAIL_FLOWS).map(([key, flow]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedFlow(key);
                        setSelectedStep(1);
                      }}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        selectedFlow === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{flow.name}</span>
                        <span className="text-xs text-gray-500">{flow.steps.length} steps</span>
                      </div>
                      <p className="text-sm text-gray-600">{flow.description}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {flow.applicableTiers.map(tier => (
                          <span 
                            key={tier}
                            className={`px-2 py-0.5 text-xs rounded ${
                              tier === selectedUser.subscriptionTier
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {tier}
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Step Selection */}
                {selectedFlow && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Starting Step:</p>
                    <div className="flex flex-wrap gap-2">
                      {EMAIL_FLOWS[selectedFlow].steps.map(step => (
                        <button
                          key={step.step}
                          onClick={() => setSelectedStep(step.step)}
                          className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                            selectedStep === step.step
                              ? 'border-blue-500 bg-blue-100 text-blue-700'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <span className="font-medium">Step {step.step}</span>
                          <span className="text-xs block text-gray-500 mt-0.5">
                            {step.templateId}
                          </span>
                          <span className="text-xs block text-gray-400">
                            {formatDelay(step.delay)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warning for tier mismatch */}
                {selectedFlow && !EMAIL_FLOWS[selectedFlow].applicableTiers.includes(selectedUser.subscriptionTier) && (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Tier Mismatch</p>
                      <p className="text-sm text-yellow-700">
                        This flow is typically for {EMAIL_FLOWS[selectedFlow].applicableTiers.join(', ')} users, 
                        but the selected user is on {selectedUser.subscriptionTier}.
                      </p>
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setSelectedFlow(selectedUser.emailFlow?.currentFlow || null);
                      setSelectedStep(selectedUser.emailFlow?.currentStep || 1);
                    }}
                    disabled={saving}
                  >
                    Reset
                  </Button>
                  <Button 
                    variant="primary"
                    onClick={saveFlowChanges}
                    disabled={saving || !selectedFlow}
                  >
                    {saving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedUser && !loading && searchResults.length === 0 && !initialUserId && (
          <Card>
            <div className="text-center py-12">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a User</h3>
              <p className="text-gray-600">
                Search for a user above to modify their email flow.
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmailFlowChangePage;
